import React, { useMemo, useState } from "react";
import { getChatPartnerId } from "../../../utils/chat";
import { MenuProps } from "antd";
import { useTranslation } from "react-i18next";
import { useIsHost } from "../../../utils/helpers";
import { useContextHospi } from "../../../context/ContextHospi";
import { useConfirmModal } from "../../../context/ContextConfirmModal";
import { useNotifier } from "../../../context/ContextNotifier";
import { ChatStatus, IFulfilledChat } from "../../../interfaces/interfacesChat";
import { SendInviteModal } from "../SendInviteModal";
import Modal from "../../Modal";
import { DialogWrapper } from "../../containers";
import { Button, TextPlain, TitleH2 } from "../../generic";
import {
  useArchiveChat,
  useReportChatAbuseMutation,
  useUnArchiveChat
} from "API/queries/chatApiQueries";
import {
  useGetMatch,
  useStudentAcceptInvite
} from "../../../API/queries/studentApiQueries";
import { MatchStatusEnum } from "../../../utils/constants";
import {
  useHostCancelInviteWithConfirmation,
  useHostRemoveTenantWithConfirmation,
  useStudentRejectInvite
} from "../../StudentInviteBox/hooks";

export const useChatDropdownMenu = ({
  activeChat
}: {
  activeChat?: IFulfilledChat;
}) => {
  const { t } = useTranslation("chats");
  const { mutate: reportAbuse } = useReportChatAbuseMutation({});
  const archiveChat = useArchiveChat();
  const unArchiveChat = useUnArchiveChat();
  const isHost = useIsHost();
  const { userProfile } = useContextHospi();
  const confirmModal = useConfirmModal();
  const notifier = useNotifier();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showReportAbuseModalSuccessful, setShowReportAbuseModalSuccessful] =
    useState(false);
  const hostCancelInvite = useHostCancelInviteWithConfirmation();
  const hostRemoveTenant = useHostRemoveTenantWithConfirmation();
  const studentRejectInvite = useStudentRejectInvite();

  const {
    mutate: studentAcceptInvite,
    isLoading: isLoadingStudentAcceptInvite
  } = useStudentAcceptInvite();

  const { data: match } = useGetMatch(
    activeChat?.student_id,
    activeChat?.host_id,
    activeChat?.house_id,
    Boolean(isHost)
  );

  const items = useMemo(() => {
    if (
      !activeChat ||
      !userProfile?.user_id ||
      !userProfile.user_type ||
      (isHost && !match)
    ) {
      return [];
    }
    const menuItems = [
      isHost &&
        match &&
        activeChat.chat_status === ChatStatus.ACCEPTED && {
          label: [
            MatchStatusEnum.INVITED,
            MatchStatusEnum.ACCEPTED,
            MatchStatusEnum.PENDING,
            MatchStatusEnum.ACTIVE
          ].includes(match.match_status!)
            ? t("modalMessages.editInvite")
            : t("modalMessages.invite"),
          key: t("modalMessages.editInvite"),
          className: "dropdown-item",
          onClick: () => {
            setShowInviteModal(true);
          }
        },
      isHost &&
        match &&
        [
          MatchStatusEnum.INVITED,
          MatchStatusEnum.ACCEPTED,
          MatchStatusEnum.PENDING,
          MatchStatusEnum.ACTIVE
        ].includes(match.match_status!) &&
        activeChat.chat_status === ChatStatus.ACCEPTED && {
          label: t("hooks.cancelInvite"),
          key: "Cancel Invite",
          className: "dropdown-item",
          onClick: () => {
            if ([MatchStatusEnum.INVITED].includes(match.match_status!)) {
              hostCancelInvite({
                host_id: activeChat.host_id,
                student_id: activeChat.student_id,
                house_id: activeChat.house_id
              });
            }
            if (
              [
                MatchStatusEnum.ACCEPTED,
                MatchStatusEnum.PENDING,
                MatchStatusEnum.ACTIVE
              ].includes(match.match_status!)
            ) {
              hostRemoveTenant({
                host_id: activeChat.host_id,
                student_id: activeChat.student_id,
                house_id: activeChat.house_id
              });
            }
          }
        },
      !isHost &&
        match &&
        [MatchStatusEnum.INVITED].includes(match.match_status!) && {
          label: t("hostAcceptRequestMessage.buttonAccept"),
          key: "Accept Invite",
          className: "dropdown-item",
          disabled: isLoadingStudentAcceptInvite,
          onClick: () => {
            studentAcceptInvite(
              {
                host_id: activeChat.host_id,
                student_id: activeChat.student_id,
                house_id: activeChat.house_id
              },
              {
                onSuccess: () => {
                  notifier.success({
                    message: t("modalMessages.inviteAccepted")
                  });
                }
              }
            );
          }
        },
      !isHost &&
        match &&
        [MatchStatusEnum.INVITED].includes(match.match_status!) && {
          label: t("hostAcceptRequestMessage.cancelInvite"),
          key: "Cancel Invite",
          className: "dropdown-item",
          onClick: () => {
            studentRejectInvite({
              host_id: activeChat.host_id,
              student_id: activeChat.student_id,
              house_id: activeChat.house_id
            });
          }
        },
      ((isHost && !activeChat.removed_by_host) ||
        (!isHost && !activeChat.removed_by_student)) && {
        label: t("modalMessages.archive"),
        key: "2",
        className: "dropdown-item",
        disabled: archiveChat.isLoading,
        onClick: () => {
          confirmModal.show({
            title: t("modalMessages.archive"),
            content: t("modalMessages.archiveQuestion"),
            onConfirm: async () => {
              return new Promise((resolve) => {
                archiveChat.mutate(
                  {
                    chatId: activeChat.chat_id
                  },
                  {
                    onSuccess: () => {
                      notifier.info({
                        message: t(
                          "archiveChatRequestModal.chatArchivedMessage"
                        ),
                        placement: "topRight"
                      });
                      resolve();
                    }
                  }
                );
              });
            },
            confirmButtonText: t("modalMessages.archive"),
            confirmButtonProps: { danger: true },
            cancelButtonText: t("butCancel")
          });
        }
      },
      ((isHost && activeChat.removed_by_host) ||
        (!isHost && activeChat.removed_by_student)) && {
        label: t("modalMessages.unArchive"),
        key: "2",
        className: "dropdown-item",
        disabled: unArchiveChat.isLoading,
        onClick: () => {
          confirmModal.show({
            title: t("modalMessages.unArchive"),
            content: t("modalMessages.unArchiveQuestion"),
            onConfirm: async () => {
              return new Promise((resolve) => {
                unArchiveChat.mutate(
                  {
                    chatId: activeChat.chat_id
                  },
                  {
                    onSuccess: () => {
                      notifier.info({
                        message: t("modalMessages.unArchiveMessage"),
                        placement: "topRight"
                      });
                      resolve();
                    }
                  }
                );
              });
            },
            confirmButtonText: t("modalMessages.unArchive"),
            confirmButtonProps: { danger: true },
            cancelButtonText: t("butCancel")
          });
        }
      },
      {
        type: "divider"
      },
      {
        label: t("hooks.report"),
        key: "3",
        danger: true,
        className: "dropdown-item",
        onClick: () => {
          confirmModal.show({
            title: `${t("report.title")} ${
              activeChat.conversation_partner_short?.first_name
            }`,
            content: t("report.text"),
            onConfirm: async () => {
              await reportAbuse(
                {
                  chatId: activeChat.chat_id,
                  reporterId: userProfile?.user_id,
                  reporteeId: getChatPartnerId(
                    activeChat,
                    userProfile.user_type
                  )!
                },
                {
                  onSuccess: () => {
                    notifier.info({
                      message: t("report.sendReport"),
                      placement: "topRight"
                    });
                  }
                }
              );
              setShowReportAbuseModalSuccessful(true);
            },
            confirmButtonText: t("report.butSendreport"),
            confirmButtonProps: { danger: true },
            cancelButtonText: t("butCancel")
          });
        }
      }
    ].filter((v) => Boolean(v)) as MenuProps["items"];

    return menuItems?.filter((item, index, all) => {
      if (
        item &&
        "type" in item &&
        item?.type === "divider" &&
        (index === 0 || index === all.length - 1)
      ) {
        return false;
      }
      return true;
    });
  }, [
    userProfile?.user_id,
    userProfile?.user_type,
    activeChat,
    confirmModal,
    reportAbuse,
    isHost,
    notifier,
    archiveChat,
    unArchiveChat,
    match,
    isLoadingStudentAcceptInvite,
    t,
    hostCancelInvite,
    hostRemoveTenant,
    studentAcceptInvite,
    studentRejectInvite
  ]);

  return {
    dropdownMenuComponents: (
      <>
        {showInviteModal &&
        activeChat?.conversation_partner_short &&
        userProfile?.user_id ? (
          <SendInviteModal
            hostId={userProfile.user_id}
            houseId={activeChat.house_id}
            student={activeChat.conversation_partner_short}
            isShow={true}
            onClose={() => setShowInviteModal(false)}
            onSent={() => {
              setShowInviteModal(false);
            }}
          />
        ) : null}
        {showReportAbuseModalSuccessful ? (
          <Modal
            isShow={true}
            handleClose={() => setShowReportAbuseModalSuccessful(false)}
          >
            <DialogWrapper>
              <TitleH2>{t("info")}</TitleH2>
              <TextPlain style={{ wordBreak: "break-word" }}>
                {t("report.sendReport")}
              </TextPlain>
              <div className="buttons">
                <span />
                <Button
                  normal
                  onClick={() => setShowReportAbuseModalSuccessful(false)}
                  style={{ width: "6em" }}
                >
                  {t("modalMessages.ok")}
                </Button>
              </div>
            </DialogWrapper>
          </Modal>
        ) : null}
      </>
    ),
    dropdownMenuItems: items
  };
};
